import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon, type SemanticICONS } from 'semantic-ui-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getCurrentOrganization,
  getLeadershipTeams,
  isCurrentMemberOrgAdmin,
  isCurrentMemberTeamLeader,
  isCurrentUserStaffOrSuperAdmin,
} from 'common/selectors/auth';
import { getMobileNavData } from './MobileNavData';

type MobileNavButtonDescriptor = {
  type: 'close' | 'custom';
  icon?: string;
  onClick: () => void;
};

type MobileNavButton = React.ReactNode | MobileNavButtonDescriptor;

const isMobileNavButtonDescriptor = (button: MobileNavButton | null | undefined): button is MobileNavButtonDescriptor =>
  !!button &&
  typeof button === 'object' &&
  'type' in button &&
  (button as any).type !== undefined &&
  'onClick' in button &&
  typeof (button as any).onClick === 'function';

interface MobileNavProps {
  topBarContent?: React.ReactNode;
  navButton?: MobileNavButton | null;
}

const MobileNav: React.FC<MobileNavProps> = ({ topBarContent, navButton }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(location.pathname);
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const isStaffOrSuperAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const leadershipTeams = useSelector(getLeadershipTeams);
  const currentOrganization = useSelector(getCurrentOrganization);
  const teamLeaderDashboardOption = (currentOrganization as any)?.organizationOptions?.find((option: any) => option.key === 'team_leader_dashboard');
  const isTeamLeaderDashboardEnabled = Boolean(teamLeaderDashboardOption?.enabled);
  const teamLeaderDashboardAudience = (teamLeaderDashboardOption?.config?.audience as string) || 'all_leaders';
  const hasLeadershipTeams = Array.isArray(leadershipTeams) && leadershipTeams.length > 0;
  const navItems = getMobileNavData({
    isTeamLeader,
    isOrgAdmin,
    isStaffOrSuperAdmin,
    hasLeadershipTeams,
  });
  const filteredNavItems = navItems.filter(item => {
    if (item.key !== 'leaderboard') return true;
    if (!isTeamLeaderDashboardEnabled) return false;
    if (teamLeaderDashboardAudience === 'organization_admin_only' && isTeamLeader) return false;
    return true;
  });

  useEffect(() => {
    setSelected(location.pathname);
  }, [location.pathname]);

  const isTopLevelMobilePath = (pathname: string) =>
    /^\/app\/mobile(?:\/(?:team-leader|org-admin|staff|knowledge-base|glossary|leaderboard|culture-hub|interventions|focus-areas)?)?$/.test(pathname);

  const handleNavigate = (url: string) => {
    setShow(false);
    navigate(url);
  };

  const showBackButton = !isTopLevelMobilePath(location.pathname);
  const customNavButton = isMobileNavButtonDescriptor(navButton) ? navButton : null;
  const navButtonNode = navButton && !isMobileNavButtonDescriptor(navButton) ? navButton : null;

  const iconName = customNavButton
    ? ((customNavButton.type === 'close'
        ? 'close'
        : (customNavButton.icon as SemanticICONS | undefined)) as SemanticICONS)
    : showBackButton
    ? ('chevron left' as SemanticICONS)
    : ((show ? 'close' : 'bars') as SemanticICONS);

  useEffect(() => {
    setSelected(location.pathname);
    if (showBackButton) {
      setShow(false);
    }
  }, [location.pathname, showBackButton]);

  return (
    <>
      <Container>
        {customNavButton ? (
          <HamburgerButton onClick={customNavButton.onClick}>
            <Icon name={iconName} />
          </HamburgerButton>
        ) : navButtonNode ? (
          <NavButtonWrapper>{navButtonNode}</NavButtonWrapper>
        ) : (
          <HamburgerButton onClick={() => (showBackButton ? navigate(-1) : setShow(!show))}>
            <Icon name={iconName} />
          </HamburgerButton>
        )}
        <Title>nuLogic</Title>
      </Container>
      {topBarContent ? <TopBarRow>{topBarContent}</TopBarRow> : null}
      {show && <Backdrop onClick={() => setShow(false)} />}
      <Drawer show={show}>
        <DrawerBody>
          {filteredNavItems.map((item) => (
            <React.Fragment key={item.key}>
              {item.key === 'logout' && <DrawerDivider />}
              <DrawerLink
                onClick={() => handleNavigate(item.path)}
                selected={selected === item.path}
                role="button"
                tabIndex={0}
              >
                <Icon name={item.icon as SemanticICONS} />
                <LinkText>{item.title}</LinkText>
              </DrawerLink>
            </React.Fragment>
          ))}
        </DrawerBody>
      </Drawer>
    </>
  );
};

export default MobileNav;

const Container = styled.div`
  width: 100vw;
  height: 70px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #000000;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 140;
`;

const HamburgerButton = styled.button`
  position: absolute;
  top: 45%;
  left: 16px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #ffffff;
  font-size: 22px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
`;

const Drawer = styled.div<{ show: boolean }>`
  position: fixed;
  top: 70px;
  left: 0;
  width: 80vw;
  max-width: 320px;
  height: calc(100vh - 70px);
  transform: translateX(${(props) => (props.show ? '0' : '-100%')});
  transition: transform 220ms ease;
  background: #ffffff;
  z-index: 150;
  box-shadow: none;
  display: flex;
  flex-direction: column;
`;

const NavButtonWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
`;

const DrawerBody = styled.div`
  padding: 12px 0;
  overflow-y: auto;
  flex: 1;
`;

const DrawerDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 12px 0 6px;
`;

const DrawerLink = styled.button<{ selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: ${(props) => (props.selected ? '#eef2ff' : 'transparent')};
  border: none;
  color: #1f2937;
  text-align: left;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    background: #f8fafc;
  }
`;

const TopBarRow = styled.div`
  position: sticky;
  top: 70px;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 120;
  display: flex;
  justify-content: flex-start;
  background: #f5f5f7;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  overflow: visible;

  .ui.dropdown .menu {
    z-index: 125 !important;
  }
`;

const LinkText = styled.span`
  font-weight: 600;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 70px 0 0 0;
  background: rgba(0, 0, 0, 0.12);
  z-index: 120;
`;
